import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export function useApi(league = null) {
  const [teams, setTeams] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [stats, setStats] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [accuracyByLeague, setAccuracyByLeague] = useState([]);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [error, setError] = useState(null);

  const leagueParam = league ? `?league=${league}` : '';

  const fetchTeams = useCallback(async (leagueOverride) => {
    try {
      const lg = leagueOverride ?? league;
      const param = lg ? `?league=${lg}` : '';
      const res = await axios.get(`${API_URL}/teams${param}`);
      setTeams(res.data.teams);
      return res.data.teams;
    } catch (err) {
      console.error('Error fetching teams:', err);
      return [];
    }
  }, [league]);

  const fetchRecentMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/matches/recent?limit=20${league ? `&league=${league}` : ''}`);
      setRecentMatches(res.data);
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
  }, [league]);

  const fetchUpcomingMatches = useCallback(async (offset = 0, filters = {}) => {
    try {
        const params = new URLSearchParams({ limit: 20, offset });
        if (league) params.set('league', league);
        if (filters.predicted_result) params.set('predicted_result', filters.predicted_result);
        if (filters.min_confidence != null) params.set('min_confidence', filters.min_confidence);
        if (filters.max_confidence != null) params.set('max_confidence', filters.max_confidence);

        const res = await axios.get(`${API_URL}/matches/upcoming?${params}`);
        setUpcomingMatches(res.data.upcoming_matches || []);
        return {
            matches: res.data.upcoming_matches || [],
            totalCount: res.data.total_count || 0,
            hasNext: res.data.has_next,
            hasPrev: res.data.has_prev
        };
    } catch (err) {
        console.error('Error fetching upcoming matches:', err);
        return { matches: [], totalCount: 0, hasNext: false, hasPrev: false };
    }
}, [league]);

  const fetchTopPicks = useCallback(async (offset = 0, leagueFilter = null) => {
    try {
        const params = new URLSearchParams({
            limit: 50,
            offset,
            min_confidence: 0.70
        });
        if (leagueFilter) params.set('league', leagueFilter);
        const res = await axios.get(`${API_URL}/matches/top-picks?${params}`);
        const picks = res.data.top_picks || [];
        setTopPicks(picks);
        return {
            picks,
            totalCount: res.data.total_count || 0,
            hasNext: res.data.has_next,
            hasPrev: res.data.has_prev
        };
    } catch (err) {
        console.error('Error fetching top picks:', err);
        return { picks: [], totalCount: 0, hasNext: false, hasPrev: false };
    }
}, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/stats${leagueParam}`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [league]);

  const fetchAccuracy = useCallback(async (season = 'all') => {
    try {
        const params = new URLSearchParams();
        if (league) params.set('league', league);
        if (season && season !== 'all') {
            const startYear = parseInt(season.split('-')[0]);
            params.set('date_from', `${startYear}-08-01`);
            params.set('date_to',   `${startYear + 1}-07-31`);
        }
        const query = params.toString() ? `?${params.toString()}` : '';
        const res = await axios.get(`${API_URL}/accuracy${query}`);
        setAccuracy(res.data);
    } catch (err) {
        console.error('Error fetching accuracy:', err);
    }
  }, [league]);

  const fetchAccuracyByLeague = useCallback(async (season = 'all') => {
    try {
        const param = (season && season !== 'all') ? `?season=${season}` : '';
        const res = await axios.get(`${API_URL}/accuracy/by-league${param}`);
        setAccuracyByLeague(res.data.by_league || []);
    } catch (err) {
        console.error('Error fetching accuracy by league:', err);
    }
}, []);

  // Carga las temporadas disponibles una sola vez al inicio,
  // siempre sin filtro para obtener todos los date_range reales
  const fetchAvailableSeasons = useCallback(async () => {
  try {
    const res = await axios.get(`${API_URL}/accuracy/by-league`);
    const leagues = res.data.by_league || [];

    if (!leagues.length) {
      setAvailableSeasons(['2024-25']);
      return;
    }

    // date_range viene como "2018-08-10 → 2026-05-17" (solo min y max)
    // Hay que rellenar todas las temporadas intermedias
    const toStartYear = (dateStr) => {
      const [year, month] = dateStr.split('-').map(Number);
      return month >= 8 ? year : year - 1;
    };

    let globalMin = Infinity;
    let globalMax = -Infinity;

    leagues.forEach(lg => {
      const dates = lg.date_range?.match(/\d{4}-\d{2}-\d{2}/g) || [];
      if (dates.length >= 2) {
        globalMin = Math.min(globalMin, toStartYear(dates[0]));
        globalMax = Math.max(globalMax, toStartYear(dates[dates.length - 1]));
      }
    });

    if (globalMin === Infinity) {
      setAvailableSeasons(['2024-25']);
      return;
    }

    const seasons = [];
    for (let y = globalMax; y >= globalMin; y--) {
      seasons.push(`${y}-${String(y + 1).slice(2)}`);
    }

    setAvailableSeasons(seasons.length ? seasons : ['2024-25']);
  } catch (err) {
    console.error('Error fetching available seasons:', err);
    setAvailableSeasons(['2024-25']);
  }
}, []);

  const fetchPatterns = useCallback(async (season = 'all') => {
    try {
      const params = new URLSearchParams();
      if (league)                     params.set('league', league);
      if (season && season !== 'all') params.set('season', season);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API_URL}/analysis/patterns${query}`);
      setPatterns(res.data);
    } catch (err) {
      console.error('Error fetching patterns:', err);
    }
  }, [league]);

  const fetchPredictionDetail = useCallback(async (matchId) => {
    const res = await axios.get(`${API_URL}/predictions/${matchId}`);
    return res.data;
  }, []);

  const predict = useCallback(async (homeTeam, awayTeam, leagueId) => {
    const res = await axios.post(`${API_URL}/predict`, {
      home_team: homeTeam,
      away_team: awayTeam,
      league: leagueId,
    });
    return res.data;
  }, []);

const fetchMatchContext = useCallback(async (matchId) => {
  try {
    const res = await axios.get(`${API_URL}/matches/${matchId}/context`);
    return res.data;
  } catch (err) {
    console.error('Error fetching match context:', err);
    return null;
  }
}, []);

  useEffect(() => {
    fetchTeams();
    fetchRecentMatches();
    fetchUpcomingMatches();
    fetchStats();
    fetchAccuracy();
  }, [league]);

  useEffect(() => {
    fetchTopPicks();
    fetchAccuracyByLeague();
    fetchAvailableSeasons();
  }, []);

  return {
    teams,
    recentMatches,
    upcomingMatches,
    topPicks,
    stats,
    accuracy,
    accuracyByLeague,
    availableSeasons,
    patterns,
    error,
    setError,
    fetchUpcomingMatches,
    fetchAccuracy,
    fetchPatterns,
    fetchTopPicks,
    fetchAccuracyByLeague,
    fetchPredictionDetail,
    predict,
    fetchTeams,
    fetchMatchContext,
  };
}